'use client';

import * as React from 'react';
import { useMemo, useCallback, startTransition } from 'react';
import { cn } from '@/lib/utils';
import { useProducts } from '@/hooks/use-products';
import { useCartStore } from '@/stores/cart-store';
import { useShallow } from 'zustand/react/shallow';
import {
  CategorySection,
  ProductSkeletonGrid,
  SetOfTheDayCard,
  SetOfTheDayPair,
} from '@/components/features/products';
import { useCatalogViewStore, selectMobileViewModeSafe } from '@/stores/catalog-view-store';
import { AnimatedBlocksSlider } from '@/components/features/hero';
import { type ProductWithCategories } from '@/lib/api/products';
import { useCategories } from '@/hooks/use-categories';
import { toast } from 'sonner';
import { ymAddToCart, ymProductView, ecomAdd, ecomDetail, tmrAddToCart, tmrProductView } from '@/lib/analytics';

export interface CatalogProps {
  deliveryPointId?: number;
  className?: string;
  onProductClick?: (product: ProductWithCategories) => void;
}

export function Catalog({
  className,
  onProductClick,
}: CatalogProps) {
  // Fetch products
  const { data: products, isLoading, error } = useProducts();
  
  // Fetch categories for sort_order
  const { data: apiCategories } = useCategories();

  // Cart store - используем селекторы для предотвращения лишних ре-рендеров
  // Получаем только actions (они стабильны и не меняются)
  const addItem = useCartStore((state) => state.addItem);
  const updateQuantity = useCartStore((state) => state.updateQuantity);
  
  // Получаем items только для расчёта quantity (с shallow comparison)
  const cartItems = useCartStore(useShallow((state) => state.items));

  // Mobile view mode (horizontal scroll vs 2x2 grid).
  // Safe-селектор гарантирует 'horizontal' до rehydrate localStorage
  // → SSR и client первый рендер совпадают (нет hydration mismatch grid'а).
  const mobileViewMode = useCatalogViewStore(selectMobileViewModeSafe);
  
  // Мемоизируем Map для быстрого поиска количества товара
  const cartQuantityMap = useMemo(() => {
    const map = new Map<number, number>();
    cartItems.forEach((item) => {
      // Тот же предикат, что в addItem/updateQuantity стора: подарок по промокоду
      // и приз рулетки лежат с isBonus:false + bonusType. Без проверки bonusType
      // карточка считала подарок покупкой — степпер не работал (updateQuantity такие
      // строки не трогает), а количество платной позиции затиралось количеством подарка.
      if (!item.isBonus && !item.bonusType) {
        map.set(item.id, item.quantity);
      }
    });
    return map;
  }, [cartItems]);

  // Стабильная функция для получения количества
  const getItemQuantity = useCallback(
    (productId: number) => cartQuantityMap.get(productId) || 0,
    [cartQuantityMap]
  );

  const filteredProducts = useMemo(() => products ?? [], [products]);

  // Категории, которые публичный API считает витринными. Ими ограничена
  // раскладка каталога: служебные позиции (подарочные роллы, доставка) в меню
  // не показываются ни на одном экране.
  const allowedCategoryIds = useMemo(
    () => (apiCategories ? new Set(apiCategories.map((c) => c.id)) : null),
    [apiCategories]
  );

  // Group filtered products by category, sorted by sort_order
  // Only show categories returned by public API (visible + have visible products)
  const filteredByCategory = useMemo(() => {
    // Build a set of allowed category IDs from the public API
    const allowedIds = allowedCategoryIds;

    const grouped = new Map<
      number,
      { category: { id: number; name: string }; products: ProductWithCategories[] }
    >();

    filteredProducts.forEach((product) => {
      product.categories.forEach((category) => {
        // Skip categories not in the public API response (hidden or no visible products)
        if (allowedIds && !allowedIds.has(category.id)) return;

        if (!grouped.has(category.id)) {
          grouped.set(category.id, {
            category,
            products: [],
          });
        }
        grouped.get(category.id)!.products.push(product);
      });
    });

    // Sort by sort_order from API categories
    if (apiCategories && apiCategories.length > 0) {
      const orderMap = new Map(apiCategories.map((c) => [c.id, c.sort_order]));
      const sorted = new Map(
        [...grouped.entries()].sort(([aId], [bId]) => {
          const aOrder = orderMap.get(aId) ?? aId;
          const bOrder = orderMap.get(bId) ?? bId;
          return aOrder - bOrder;
        })
      );
      return sorted;
    }

    return grouped;
  }, [filteredProducts, apiCategories, allowedCategoryIds]);

  // Активные "Сеты дня" — вырезаем их из обычной сетки, чтобы не было дубликата.
  // Поддерживаем 1 или 2 сета: 1 → одиночный full-width баннер, 2 → пара
  // (на ПК две колонки, на мобиле carousel со свайпом и автоскроллом).
  // Если в админке отметили больше — берём первые 2 по id ASC.
  const setsOfTheDay = useMemo(() => {
    return filteredProducts
      .filter((p) => p.is_set_of_the_day)
      .sort((a, b) => a.id - b.id)
      .slice(0, 2);
  }, [filteredProducts]);

  // Множество id сетов — для O(1) исключения из обычной сетки внутри sortedByCategory
  const setOfTheDayIds = useMemo(() => new Set(setsOfTheDay.map((p) => p.id)), [setsOfTheDay]);

  // Категория, в которую мы поднимем промо-карточку (первая из категорий первого сета).
  // Парный блок появится после той же категории-хоста, что и одиночный.
  const setOfTheDayHostCategoryId = useMemo(() => {
    const first = setsOfTheDay[0];
    if (!first) return null;
    return first.categories[0]?.id ?? null;
  }, [setsOfTheDay]);

  // Sort products within each category — always by price ascending
  const sortedByCategory = useMemo(() => {
    const sorted = new Map<
      number,
      { category: { id: number; name: string }; products: ProductWithCategories[] }
    >();

    filteredByCategory.forEach((value, key) => {
      const products = [...value.products]
        .filter((p) => !setOfTheDayIds.has(p.id))
        .sort((a, b) => a.price - b.price);
      sorted.set(key, { category: value.category, products });
    });

    return sorted;
  }, [filteredByCategory, setOfTheDayIds]);

  // Handlers с startTransition
  const handleAddToCart = useCallback(
    (product: ProductWithCategories) => {
      startTransition(() => {
        addItem(product);
        ymAddToCart(product.id, product.name, product.price, 1);
        tmrAddToCart();
        ecomAdd({ id: String(product.id), name: product.name, price: product.price, category: product.categories?.[0]?.name });
        toast.success('Добавлено в корзину', {
          description: product.name,
        });
      });
    },
    [addItem]
  );

  const handleQuantityChange = useCallback(
    (productId: number, quantity: number) => {
      startTransition(() => {
        updateQuantity(productId, quantity);
      });
    },
    [updateQuantity]
  );

  const handleProductClick = useCallback(
    (product: ProductWithCategories) => {
      startTransition(() => {
        ymProductView(product.id, product.name, product.price);
        tmrProductView();
        ecomDetail({ id: String(product.id), name: product.name, price: product.price, category: product.categories?.[0]?.name });
        onProductClick?.(product);
      });
    },
    [onProductClick]
  );

  // Error state
  if (error) {
    return (
      <div className="py-12 text-center">
        <p className="text-[var(--error)] text-lg">
          Ошибка загрузки меню. Попробуйте обновить страницу.
        </p>
      </div>
    );
  }

  // Loading state
  if (isLoading) {
    return (
      <div className={cn('space-y-8', className)}>
        <div className="h-12 animate-pulse bg-[var(--surface-secondary)] rounded-card mr-4 sm:mr-0" />
        <ProductSkeletonGrid count={8} />
      </div>
    );
  }

  return (
    <div className={cn('space-y-8', className)}>
      {/* Animated Info Slider — перед первой категорией */}
      <AnimatedBlocksSlider className="mb-6 mr-4 sm:mr-0" />

      {/* Category Sections.
          Зазор 32, а не 48: недостающие 16 переехали внутрь секции отдельным
          pb-4 (см. category-section.tsx) — там они дают тени нижнего ряда
          карточек место внутри paint-изоляции секции. Расстояние между
          секциями осталось прежним: 32 зазора + 16 отступа = 48.
          Меняете одно — правьте второе, иначе поедет вертикаль всего каталога. */}
      <div className="space-y-8">
        {Array.from(sortedByCategory.entries()).map(([categoryId, { category, products }], index) => {
          const isHostCategory = setsOfTheDay.length > 0 && categoryId === setOfTheDayHostCategoryId;
          const section = (
            <CategorySection
              id={`category-${categoryId}`}
              // Прокрутка по чипу останавливается НИЖЕ липкой панели (её высота
              // ~124px + 72px до неё), иначе заголовок категории оказывается за
              // ней и нажатие на чип выглядит как «пролистнуло не туда».
              className="scroll-mt-[calc(var(--app-banner-h,0px)_+_200px)]"
              title={category.name}
              products={products}
              getQuantity={getItemQuantity}
              onAddToCart={handleAddToCart}
              onQuantityChange={handleQuantityChange}
              onOpenPopup={handleProductClick}
              isFirstSection={index <= 1}
              mobileGridMode={mobileViewMode === 'grid'}
              showViewToggle={index === 0}
            />
          );
          if (isHostCategory) {
            return (
              // pb-4 и mt-2 — та же арифметика, что и у обычной секции:
              // 16 отступа секции + 8 сверху = прежние 24 до «Сета дня»,
              // 16 снизу обёртки + 32 зазора = прежние 48 до следующей секции.
              <div key={categoryId} className="pb-4">
                {section}
                <div className="mt-2 mr-4 sm:mr-0">
                  {setsOfTheDay.length === 1 ? (
                    <SetOfTheDayCard product={setsOfTheDay[0]} onClick={handleProductClick} />
                  ) : (
                    <SetOfTheDayPair
                      products={[setsOfTheDay[0], setsOfTheDay[1]]}
                      onClick={handleProductClick}
                    />
                  )}
                </div>
              </div>
            );
          }
          return <React.Fragment key={categoryId}>{section}</React.Fragment>;
        })}
      </div>

      {/* Меню пустое (сервер отдал ноль товаров) */}
      {filteredProducts.length === 0 && !isLoading && (
        <div className="py-12 text-center">
          <p className="text-[var(--text-secondary)] text-lg">
            Меню сейчас недоступно. Попробуйте обновить страницу.
          </p>
        </div>
      )}
    </div>
  );
}

